document.documentElement.classList.add("hm-has-js");

(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-hm-section], .hm-page, .hybrid-compact";
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const topLevelRoots = (scope = document) =>
    Array.from(scope.querySelectorAll(ROOT_SELECTOR)).filter(
      (candidate) =>
        !candidate.parentElement ||
        !candidate.parentElement.closest(ROOT_SELECTOR)
    );

  const normaliseStoreRoot = () => {
    const configuredRoot = window.Shopify?.routes?.root || "/";
    return configuredRoot.endsWith("/")
      ? configuredRoot
      : `${configuredRoot}/`;
  };

  const revealItems = (scope) => {
    const items = scope.querySelectorAll(
      ".hm-reveal:not([data-hm-reveal-bound]), [data-hm-reveal]:not([data-hm-reveal-bound])"
    );

    if (!items.length) return;

    if (reducedMotion.matches || !("IntersectionObserver" in window)) {
      items.forEach((item) => {
        item.dataset.hmRevealBound = "true";
        item.classList.add("is-visible");
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries, revealObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          revealObserver.unobserve(entry.target);
        });
      },
      {
        threshold: 0.08,
        rootMargin: "0px 0px -24px"
      }
    );

    items.forEach((item) => {
      item.dataset.hmRevealBound = "true";
      observer.observe(item);
    });

    // Never leave content hidden if an embedded browser drops observer events.
    window.setTimeout(() => {
      items.forEach((item) => item.classList.add("is-visible"));
      observer.disconnect();
    }, 4000);
  };

  const bindFaqs = (root) => {
    const details = root.querySelectorAll(
      ".hm-faq details, .hm-faq__list details, [data-hm-faq] details"
    );

    details.forEach((item) => {
      if (item.dataset.hmFaqBound === "true") return;
      item.dataset.hmFaqBound = "true";

      item.addEventListener("toggle", () => {
        if (!item.open) return;
        details.forEach((other) => {
          if (other !== item) other.open = false;
        });
      });
    });
  };

  const bindMobileMenu = (root) => {
    const button = root.querySelector("[data-hm-menu-button]");
    const navigation = root.querySelector(".hm-nav");

    if (
      !button ||
      !navigation ||
      button.dataset.hmMenuBound === "true"
    ) {
      return;
    }

    button.dataset.hmMenuBound = "true";

    const closeMenu = () => {
      navigation.classList.remove("is-open");
      button.setAttribute("aria-expanded", "false");
      button.setAttribute("aria-label", "Open navigation");
    };

    button.addEventListener("click", () => {
      const open = !navigation.classList.contains("is-open");
      navigation.classList.toggle("is-open", open);
      button.setAttribute("aria-expanded", String(open));
      button.setAttribute(
        "aria-label",
        open ? "Close navigation" : "Open navigation"
      );
    });

    navigation.querySelectorAll("a").forEach((link) => {
      link.addEventListener("click", closeMenu);
    });

    root.ownerDocument.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeMenu();
    });
  };

  const ensureErrorNode = (form) => {
    const existing =
      form.querySelector("[data-hm-error], .hm-product-error") ||
      form.parentElement?.querySelector("[data-hm-error], .hm-product-error");

    if (existing) {
      existing.setAttribute("role", "alert");
      existing.setAttribute("aria-live", "assertive");
      if (!existing.hasAttribute("tabindex")) existing.tabIndex = -1;
      return existing;
    }

    const node = document.createElement("div");
    node.className = "hm-product-error";
    node.dataset.hmError = "";
    node.hidden = true;
    node.tabIndex = -1;
    node.setAttribute("role", "alert");
    node.setAttribute("aria-live", "assertive");
    form.prepend(node);
    return node;
  };

  const parseResponse = async (response) => {
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      return response.json();
    }

    const text = await response.text();
    return text ? { description: text } : {};
  };

  const bindProductForm = (root, form) => {
    if (form.dataset.hmFormBound === "true") return;
    form.dataset.hmFormBound = "true";

    const button = form.querySelector(
      "[data-hm-submit], button[type='submit'], input[type='submit']"
    );
    const labelNode =
      button?.querySelector(
        "[data-hm-submit-label], [data-hm-cta], .hm-button__label"
      ) || button;
    const select =
      form.querySelector("[data-hm-variant]") ||
      form.querySelector("select[name='id']");
    const error = ensureErrorNode(form);
    const priceNodes = root.querySelectorAll(
      "[data-hm-price], [data-hm-sticky-price]"
    );
    const initialLabel = labelNode?.textContent.trim() || "Get Hybrid Method";
    const defaultLabel =
      labelNode?.dataset.hmDefaultLabel ||
      initialLabel
        .replace(/\s[-\u2013\u2014]\s[^-\u2013\u2014]+$/, "")
        .trim();
    let loading = false;

    const setError = (message = "") => {
      error.textContent = message;
      error.hidden = !message;
      if (message) error.focus({ preventScroll: false });
    };

    const setLoading = (isLoading) => {
      loading = isLoading;
      if (!button) return;

      button.classList.toggle("is-loading", isLoading);
      button.setAttribute("aria-busy", String(isLoading));
      button.disabled =
        isLoading || button.dataset.hmAvailable === "false";

      if (labelNode) {
        labelNode.textContent = isLoading
          ? "Adding to checkout…"
          : labelNode.dataset.hmCurrentLabel || initialLabel;
      }
    };

    const updateVariant = () => {
      if (!button || !select) return;

      const option = select.selectedOptions?.[0];
      if (!option) return;

      const available =
        option.dataset.available === undefined
          ? !option.disabled
          : option.dataset.available === "true";
      const currentLabel = available
        ? defaultLabel
        : "Sold out";
      const price = option.dataset.price || "";

      button.dataset.hmAvailable = String(available);
      button.disabled = loading || !available;
      if (labelNode) {
        labelNode.dataset.hmCurrentLabel = currentLabel;
        if (!loading) labelNode.textContent = currentLabel;
      }
      if (price) {
        priceNodes.forEach((node) => {
          node.textContent = price;
        });
      }
    };

    select?.addEventListener("change", updateVariant);
    updateVariant();

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (loading || !button || button.disabled) return;

      setError("");
      setLoading(true);

      try {
        const storeRoot = normaliseStoreRoot();
        const response = await fetch(`${storeRoot}cart/add.js`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          body: new FormData(form),
          credentials: "same-origin"
        });
        const payload = await parseResponse(response);

        if (!response.ok) {
          throw new Error(
            payload.description ||
              payload.message ||
              "This product could not be added. Check the selected option and try again."
          );
        }

        window.location.assign(`${storeRoot}checkout`);
      } catch (requestError) {
        const message =
          requestError instanceof Error && requestError.message
            ? requestError.message
            : "Checkout could not start. Check your connection and try again.";
        setError(message);
        setLoading(false);
      }
    });

    form.hmResetLoading = () => {
      setLoading(false);
      updateVariant();
    };
  };

  const bindProductForms = (root) => {
    const forms = root.querySelectorAll(
      "form[data-hm-form], form.hm-product-form"
    );
    forms.forEach((form) => bindProductForm(root, form));
  };

  const bindMarquees = (root) => {
    root.querySelectorAll("[data-hm-marquee]").forEach((marquee) => {
      if (marquee.dataset.hmMarqueeBound === "true") return;

      const track = marquee.querySelector("[data-hm-marquee-track]");
      const originals = track
        ? Array.from(track.children).filter(
            (item) => !item.hasAttribute("data-hm-marquee-clone")
          )
        : [];

      if (!track || originals.length < 2) return;
      marquee.dataset.hmMarqueeBound = "true";

      if (reducedMotion.matches) {
        marquee.setAttribute(
          "aria-label",
          "Client results. Automatic motion is disabled by your device settings."
        );
        return;
      }

      marquee.classList.add("is-hm-enhanced");
      marquee.style.setProperty("--hm-result-count", String(originals.length));

      const mobileMode = window.matchMedia("(max-width: 809px)");
      const progress = document.createElement("div");
      progress.className = "hm-marquee__progress";
      progress.setAttribute("aria-label", "Choose a client result");
      const progressButtons = originals.map((_, index) => {
        const button = document.createElement("button");
        button.type = "button";
        button.setAttribute("aria-label", `Show client result ${index + 1}`);
        button.dataset.hmSlideTo = String(index);
        button.innerHTML = '<span aria-hidden="true"></span>';
        progress.append(button);
        return button;
      });
      marquee.append(progress);

      originals.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.dataset.hmMarqueeClone = "true";
        clone.setAttribute("aria-hidden", "true");
        clone.setAttribute("inert", "");
        clone.removeAttribute("id");
        clone.removeAttribute("data-hm-reveal");
        clone.removeAttribute("data-hm-reveal-bound");
        clone.classList.add("is-visible");
        clone.querySelectorAll("[id]").forEach((node) => node.removeAttribute("id"));
        track.append(clone);
      });

      const speed = Math.max(
        16,
        Number.parseFloat(marquee.dataset.hmMarqueeSpeed || "60") || 60
      );
      let cycleWidth = 0;
      let offset = 0;
      let lastTimestamp = 0;
      let frame = 0;
      let visible = !("IntersectionObserver" in window);
      let paused = false;
      let slideIndex = 0;
      let slideTimer = 0;

      const measure = () => {
        const firstClone = track.querySelector("[data-hm-marquee-clone]");
        if (!firstClone || !originals[0]) return;
        cycleWidth = firstClone.offsetLeft - originals[0].offsetLeft;
        if (cycleWidth > 0) offset %= cycleWidth;
      };

      const render = () => {
        if (mobileMode.matches) return;
        track.style.transform = `translate3d(${-offset}px, 0, 0)`;
      };

      const stopSlides = () => {
        if (!slideTimer) return;
        window.clearTimeout(slideTimer);
        slideTimer = 0;
      };

      const scheduleSlides = () => {
        stopSlides();
        if (
          !mobileMode.matches ||
          paused ||
          !visible ||
          document.hidden
        ) {
          return;
        }

        slideTimer = window.setTimeout(() => {
          slideTimer = 0;
          setSlide(slideIndex + 1);
        }, 4000);
      };

      const setSlide = (requestedIndex) => {
        slideIndex =
          ((requestedIndex % originals.length) + originals.length) %
          originals.length;

        originals.forEach((item, index) => {
          item.classList.toggle("is-hm-slide-active", index === slideIndex);
        });
        progressButtons.forEach((button, index) => {
          const active = index === slideIndex;
          button.classList.toggle("is-active", active);
          button.setAttribute("aria-current", active ? "true" : "false");
          const fill = button.querySelector("span");
          if (fill) {
            fill.style.animation = "none";
            void fill.offsetWidth;
            fill.style.animation = "";
          }
        });
        scheduleSlides();
      };

      const tick = (timestamp) => {
        if (!lastTimestamp) lastTimestamp = timestamp;
        const delta = Math.min(64, timestamp - lastTimestamp);
        lastTimestamp = timestamp;

        if (
          !mobileMode.matches &&
          !paused &&
          visible &&
          !document.hidden &&
          cycleWidth > 0
        ) {
          offset = (offset + (speed * delta) / 1000) % cycleWidth;
          render();
        }

        frame = window.requestAnimationFrame(tick);
      };

      const pause = () => {
        paused = true;
        stopSlides();
      };
      const resume = () => {
        paused = false;
        lastTimestamp = 0;
        scheduleSlides();
      };

      progressButtons.forEach((button) => {
        button.addEventListener("click", () => {
          setSlide(Number.parseInt(button.dataset.hmSlideTo || "0", 10));
        });
      });
      marquee.addEventListener("mouseenter", pause);
      marquee.addEventListener("mouseleave", resume);
      marquee.addEventListener("focusin", pause);
      marquee.addEventListener("focusout", () => {
        window.requestAnimationFrame(resume);
      });
      document.addEventListener("visibilitychange", () => {
        lastTimestamp = 0;
        if (document.hidden) stopSlides();
        else scheduleSlides();
      });

      const configureMode = () => {
        lastTimestamp = 0;
        stopSlides();
        if (mobileMode.matches) {
          track.style.transform = "";
          setSlide(slideIndex);
        } else {
          window.requestAnimationFrame(() => {
            measure();
            render();
          });
        }
      };
      mobileMode.addEventListener?.("change", configureMode);

      window.addEventListener(
        "resize",
        () => {
          measure();
          render();
        },
        { passive: true }
      );

      if ("ResizeObserver" in window) {
        const resizeObserver = new ResizeObserver(() => {
          measure();
          render();
        });
        resizeObserver.observe(track);
      }

      if ("IntersectionObserver" in window) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            visible = entry.isIntersecting;
            lastTimestamp = 0;
            if (visible) scheduleSlides();
            else stopSlides();
          },
          { rootMargin: "180px 0px", threshold: 0.01 }
        );
        observer.observe(marquee);
      }

      window.requestAnimationFrame(() => {
        measure();
        render();
        setSlide(0);
        frame = window.requestAnimationFrame(tick);
      });

      marquee.hmDestroyMarquee = () => {
        window.cancelAnimationFrame(frame);
        stopSlides();
      };
    });
  };

  const bindProofRails = (root) => {
    root.querySelectorAll("[data-hm-rail]").forEach((rail) => {
      if (rail.dataset.hmRailBound === "true") return;

      const track = rail.querySelector("[data-hm-rail-track]");
      const previous = rail.querySelector("[data-hm-rail-prev]");
      const next = rail.querySelector("[data-hm-rail-next]");
      const cards = track ? Array.from(track.children) : [];
      const autoplayDelay = Number.parseInt(
        rail.dataset.hmAutoplay || "0",
        10
      );

      if (!track || !previous || !next || !cards.length) return;
      rail.dataset.hmRailBound = "true";

      let frameRequested = false;
      let autoplayTimer = 0;
      let autoplayResumeTimer = 0;
      let railVisible = !("IntersectionObserver" in window);
      const controls = previous.closest(".hm-rail__controls");
      if (cards.length < 2 && controls) controls.hidden = true;

      const stepSize = () => {
        const cardWidth = cards[0].getBoundingClientRect().width;
        const styles = window.getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap) || 0;
        return cardWidth + gap;
      };

      const updateControls = () => {
        frameRequested = false;
        const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
        previous.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= maximum - 2;
      };

      const requestControlUpdate = () => {
        if (frameRequested) return;
        frameRequested = true;
        window.requestAnimationFrame(updateControls);
      };

      const move = (direction) => {
        track.scrollBy({
          left: stepSize() * direction,
          behavior: reducedMotion.matches ? "auto" : "smooth"
        });
      };

      const stopAutoplay = () => {
        if (!autoplayTimer) return;
        window.clearTimeout(autoplayTimer);
        autoplayTimer = 0;
      };

      const scheduleAutoplay = () => {
        stopAutoplay();
        if (
          !Number.isFinite(autoplayDelay) ||
          autoplayDelay < 3000 ||
          cards.length < 2 ||
          reducedMotion.matches ||
          document.hidden ||
          !railVisible ||
          rail.matches(":hover") ||
          rail.matches(":focus-within")
        ) {
          return;
        }

        autoplayTimer = window.setTimeout(() => {
          autoplayTimer = 0;
          const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
          const atEnd = track.scrollLeft >= maximum - stepSize() * 0.45;

          if (atEnd) {
            track.scrollTo({
              left: 0,
              behavior: reducedMotion.matches ? "auto" : "smooth"
            });
          } else {
            move(1);
          }

          scheduleAutoplay();
        }, autoplayDelay);
      };

      const pauseAutoplay = (resumeAfter = 7000) => {
        stopAutoplay();
        if (autoplayResumeTimer) {
          window.clearTimeout(autoplayResumeTimer);
          autoplayResumeTimer = 0;
        }
        if (
          !Number.isFinite(autoplayDelay) ||
          autoplayDelay < 3000 ||
          reducedMotion.matches
        ) {
          return;
        }
        autoplayResumeTimer = window.setTimeout(() => {
          autoplayResumeTimer = 0;
          scheduleAutoplay();
        }, resumeAfter);
      };

      previous.addEventListener("click", () => {
        pauseAutoplay();
        move(-1);
      });
      next.addEventListener("click", () => {
        pauseAutoplay();
        move(1);
      });
      track.addEventListener("keydown", (event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        pauseAutoplay();
        move(event.key === "ArrowLeft" ? -1 : 1);
      });
      track.addEventListener("scroll", requestControlUpdate, {
        passive: true
      });
      rail.addEventListener("pointerdown", () => pauseAutoplay(), {
        passive: true
      });
      rail.addEventListener("mouseenter", stopAutoplay);
      rail.addEventListener("mouseleave", scheduleAutoplay);
      rail.addEventListener("focusin", stopAutoplay);
      rail.addEventListener("focusout", () => {
        window.requestAnimationFrame(scheduleAutoplay);
      });
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) stopAutoplay();
        else scheduleAutoplay();
      });
      window.addEventListener("resize", requestControlUpdate, {
        passive: true
      });

      if ("ResizeObserver" in window) {
        const resizeObserver = new ResizeObserver(requestControlUpdate);
        resizeObserver.observe(track);
      }

      updateControls();

      if ("IntersectionObserver" in window && autoplayDelay >= 3000) {
        const autoplayObserver = new IntersectionObserver(
          ([entry]) => {
            railVisible = entry.isIntersecting;
            if (railVisible) scheduleAutoplay();
            else stopAutoplay();
          },
          { threshold: 0.2 }
        );
        autoplayObserver.observe(rail);
      } else {
        scheduleAutoplay();
      }
    });
  };

  const bindVideoPlayers = (root) => {
    root.querySelectorAll("[data-hm-video-player]").forEach((player) => {
      if (player.dataset.hmVideoBound === "true") return;

      const button = player.querySelector("[data-hm-video-play]");
      const videoId = player.dataset.hmVideoId || "";
      const start = Number.parseInt(player.dataset.hmVideoStart || "0", 10);

      if (!button || !/^[A-Za-z0-9_-]{11}$/.test(videoId)) return;
      player.dataset.hmVideoBound = "true";

      button.addEventListener(
        "click",
        () => {
          const iframe = document.createElement("iframe");
          const startAt = Number.isFinite(start) && start > 0 ? start : 0;
          iframe.src =
            `https://www.youtube-nocookie.com/embed/${videoId}` +
            `?autoplay=1&rel=0&start=${startAt}`;
          iframe.title = "My 100 Day Body Transformation";
          iframe.allow =
            "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
          iframe.referrerPolicy = "strict-origin-when-cross-origin";
          iframe.allowFullscreen = true;

          player.classList.add("is-playing");
          player.replaceChildren(iframe);
        },
        { once: true }
      );
    });
  };

  const bindStickyCta = (root) => {
    const sticky = root.querySelector(
      "[data-hm-sticky], .hm-sticky-cta"
    );
    if (!sticky || sticky.dataset.hmStickyBound === "true") return;
    sticky.dataset.hmStickyBound = "true";

    const offer = root.querySelector(
      "[data-hm-offer], .hm-offer"
    );
    const hero = root.querySelector(".hm-hero, [data-hm-hero]");
    let offerVisible = false;
    let frameRequested = false;

    const setVisible = (visible) => {
      sticky.classList.toggle("is-visible", visible);
      sticky.setAttribute("aria-hidden", String(!visible));
      sticky.toggleAttribute("inert", !visible);
    };

    const update = () => {
      frameRequested = false;
      const heroBottom = hero?.getBoundingClientRect().bottom ?? 0;
      const pastHero = hero ? heroBottom < 80 : window.scrollY > 480;

      if (offer && !("IntersectionObserver" in window)) {
        const offerRect = offer.getBoundingClientRect();
        offerVisible =
          offerRect.top < window.innerHeight && offerRect.bottom > 0;
      }

      setVisible(pastHero && !offerVisible);
    };

    const requestUpdate = () => {
      if (frameRequested) return;
      frameRequested = true;
      window.requestAnimationFrame(update);
    };

    if (offer && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          offerVisible = entry.isIntersecting;
          requestUpdate();
        },
        { threshold: 0.01 }
      );
      observer.observe(offer);
    }

    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate, { passive: true });
    setVisible(false);
    update();
  };

  const initialiseRoot = (root) => {
    if (!root || root.dataset.hmBound === "true") return;
    root.dataset.hmBound = "true";

    revealItems(root);
    bindMobileMenu(root);
    bindFaqs(root);
    bindProductForms(root);
    bindMarquees(root);
    bindProofRails(root);
    bindVideoPlayers(root);
    bindStickyCta(root);
  };

  const initialise = (scope = document) => {
    topLevelRoots(scope).forEach(initialiseRoot);
  };

  const onReady = () => initialise(document);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onReady, { once: true });
  } else {
    onReady();
  }

  document.addEventListener("shopify:section:load", (event) => {
    const directRoot = event.target.matches?.(ROOT_SELECTOR)
      ? event.target
      : null;
    if (directRoot) initialiseRoot(directRoot);
    initialise(event.target);
  });

  window.addEventListener("pageshow", () => {
    document
      .querySelectorAll("form[data-hm-form], form.hm-product-form")
      .forEach((form) => form.hmResetLoading?.());
  });
})();
